/* eslint-disable react/display-name*/
import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { useColorScheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { all, compose, filter, includes, isEmpty, sort } from 'ramda';
import { isNilOrEmpty, isNilOrEmptyString } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import useValidateFieldsPromise from '@gef-ui/form-core/hooks/form/useValidateFieldsPromise';
import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import useFormFieldValue from '@gef-ui/form-core/hooks/state/useFormFieldValue';
import { setValue } from '@gef-ui/form-core/stream/transfer/control';
import { validationDisplayTypes } from '@gef-ui/form-material/constants';
import RadioGroup from '@gef-ui/form-material/RadioGroup';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader, Dialog } from 'components';
import AutocompleteCustom from 'components/atoms/GefMui/AutocompleteCustom';
import SectionHeader from 'components/atoms/GefMui/SectionHeader';
import TextFieldWithSearchIcon from 'components/atoms/GefMui/TextFieldWithSearchIcon';
import codelistProvider from 'components/decorators/codelistProvider';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import { EMPTY_VALUES } from 'constants/general';
import MODALS from 'constants/modals';
import {
	MPU_FORWARD_TO_AGENCY,
	MPU_FORWARD_TO_SUBUSER,
	MPU_FORWARD_TO_SUPERUSER,
	MPU_SEARCH_USERS,
} from 'features/common/acl/constants';
import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import { addSingleErrorByPageAndId, removeErrorByPageAndId } from 'features/common/submitFormDialog/slice';
import { fetchUsers, searchUser } from 'features/other/resolverSettings/actions';
import { dialogs, radioOptions, radioOptionsIds } from 'features/other/resolverSettings/constants';
import { FIELDS } from 'features/other/resolverSettings/form';
import {
	getSubordinateUsers,
	getSuperiorUsers,
	getUserDataFromDBFlag,
} from 'features/other/resolverSettings/selectors';
import { setUserDataDBFlag } from 'features/other/resolverSettings/slice';
import { CONTACT_CLIENT_FORM_NAME, MANUAL_LEAD_FORM_NAME } from 'features/views/taskCreatingPages/form';
import { TASK_FORWARDING_FORM_NAME } from 'features/views/taskForwarding/form';
import { sortAlphabeticallyBy } from 'utils/sort';
import { userCodelistOptionsWithFullnamesAndLogins } from 'utils/user';
import UserSearchModal from 'pages/UserSearchModal/UserSearchModal';

export const ResolverSettings = ({
	formName,
	idkAgencies,
	idkRegions,
	showNonUniqueSolverDialog = true,
	showSectionHeader = true,
}) => {
	const dispatch = useDispatch();
	const next = useControlNext(formName);
	const validateFields = useValidateFieldsPromise();
	const { mode: theme } = useColorScheme();

	const radio = useFormFieldValue({ fieldName: FIELDS.radio });
	const region = useFormFieldValue({ fieldName: FIELDS.region });
	const formData = useFormData();

	const superiorUsers = useSelector(getSuperiorUsers);
	const subordinateUsers = useSelector(getSubordinateUsers);

	const isUserDataFromDb = useSelector(getUserDataFromDBFlag);

	const enableShowSubordinate = useSelector((state) => hasPermissionByIds(state)(MPU_FORWARD_TO_SUBUSER));
	const enableShowSuperior = useSelector((state) => hasPermissionByIds(state)(MPU_FORWARD_TO_SUPERUSER));
	const enableShowUsers = useSelector((state) => hasPermissionByIds(state)(MPU_SEARCH_USERS));
	const enableShowAgency = useSelector((state) => hasPermissionByIds(state)(MPU_FORWARD_TO_AGENCY));

	// getting rid of administrative accounts + formatting select options + appending possible login
	const sortedSuperiorsOptionsWithPossibleLogin = userCodelistOptionsWithFullnamesAndLogins(superiorUsers);
	const sortedSubordinateUsersOptionsWithPossibleLogin = userCodelistOptionsWithFullnamesAndLogins(subordinateUsers);
	const filteredRegions = useMemo(
		() =>
			compose(
				sort(sortAlphabeticallyBy('description')),
				filter((item) => {
					const isIdCorrect = item?.regionId > 100;
					return isIdCorrect;
				}),
				filter((item) => item?.channelId === 1),
				filter((item) => item?.mpuVisible),
			)(idkRegions),
		[idkRegions],
	);

	const filteredAgencies = useMemo(
		() =>
			compose(
				sort(sortAlphabeticallyBy('description')),
				filter((item) => {
					if (!isNilOrEmptyString(region)) {
						return item?.regionId === region && item?.agencyId < 1000;
					}
				}),
				filter((item) => item?.channelId === 1),
				filter((item) => item?.mpuVisible),
			)(idkAgencies),
		[idkAgencies, region],
	);

	const radioOptionsWithRequiredPermission = useMemo(() => {
		let finalRadioOptions;
		if (formName === CONTACT_CLIENT_FORM_NAME) {
			finalRadioOptions = radioOptions.filter((item) =>
				includes(item.id, [radioOptionsIds.users, radioOptionsIds.agency]),
			);
		} else if (formName === MANUAL_LEAD_FORM_NAME) {
			finalRadioOptions = radioOptions.filter((item) =>
				includes(item.id, [radioOptionsIds.users, radioOptionsIds.marketPlace]),
			);
		} else {
			// all radio options except marketPlace (exclusive for manualLead)
			finalRadioOptions = radioOptions.filter((item) => item.id !== radioOptionsIds.marketPlace);
		}
		return finalRadioOptions
			.filter((option) => {
				let returnOption = '';
				switch (option.id) {
					case radioOptionsIds.subordinate:
						returnOption = enableShowSubordinate && option;
						break;
					case radioOptionsIds.superior:
						returnOption = enableShowSuperior && option;
						break;
					case radioOptionsIds.users:
						returnOption = enableShowUsers && option;
						break;
					case radioOptionsIds.agency:
						returnOption = enableShowAgency && option;
						break;
					case radioOptionsIds.marketPlace:
						returnOption = option;
						break;
				}
				return returnOption;
			})
			.map((option) => ({ ...option, description: getLabelMessage(option.description) }));
	}, [enableShowAgency, enableShowSubordinate, enableShowSuperior, enableShowUsers, formName]);

	const handleRadioChange = useCallback(
		(e, value) => {
			const resolverSettingsFields = [
				FIELDS.subordinate,
				FIELDS.superior,
				FIELDS.agency,
				FIELDS.region,
				FIELDS.login,
				FIELDS.sellerIn,
				FIELDS.userFirstName,
				FIELDS.userSurname,
			];

			resolverSettingsFields.forEach((fieldName) => {
				next(setValue({ fieldName, value: null }));
			});

			switch (value) {
				case radioOptionsIds.subordinate:
					isEmpty(subordinateUsers) && dispatch(fetchUsers('subordinate'));
					break;
				case radioOptionsIds.superior:
					isEmpty(superiorUsers) && dispatch(fetchUsers('superior'));
					break;
				default:
					break;
			}
		},
		[dispatch, superiorUsers, subordinateUsers, next],
	);

	const handleSearchUser = useCallback(async () => {
		dispatch(removeErrorByPageAndId({ page: formName, id: 'radio' }));

		if (all(isNilOrEmptyString, [formData?.login, formData?.sellerIn, formData?.userSurname])) {
			dispatch(addSingleErrorByPageAndId({ page: formName, errors: ['loginOrSellerInOrUserSurname'] }));
			dispatch(dialog.actions.open(formName));
		} else {
			dispatch(removeErrorByPageAndId({ page: formName, id: 'loginOrSellerInOrUserSurname' }));
		}

		try {
			await validateFields([[FIELDS.login], [FIELDS.sellerIn], [FIELDS.userSurname]]);
			dispatch(searchUser(formName));
		} catch (error) {
			return;
		}
	}, [dispatch, validateFields, formData?.login, formData?.sellerIn, formData?.userSurname, formName]);

	const handleUserFieldChange = useCallback(() => {
		if (isUserDataFromDb) {
			dispatch(setUserDataDBFlag(false));
		}
	}, [dispatch, isUserDataFromDb]);

	const handleAgencyReset = useCallback(() => {
		next(setValue({ fieldName: FIELDS.agency, value: null }));
	}, [next]);

	const paperSx = useMemo(() => {
		if (formName !== TASK_FORWARDING_FORM_NAME) {
			return { p: 3, borderRadius: 2 };
		} else {
			// ResolverSettings for TaskForwarding is in modal - no need for styled paper
			if (theme === 'dark') {
				return { background: 'none' };
			} else {
				return EMPTY_VALUES.OBJECT;
			}
		}
	}, [formName, theme]);

	return (
		<Paper sx={{ ...paperSx }}>
			{showNonUniqueSolverDialog && (
				<Dialog mui name={dialogs.NON_UNIQUE_SOLVER}>
					{getLabelMessage('resolverSettings:nonUniqueSolver')}
				</Dialog>
			)}
			<UserSearchModal id={MODALS.user_search} />
			<Stack spacing={2}>
				{showSectionHeader && <SectionHeader text={getLabelMessage('resolverSettings:title')} />}
				<RadioGroup
					fieldName={FIELDS.radio}
					labelPropName="description"
					valuePropName="id"
					options={radioOptionsWithRequiredPermission}
					row
					onChange={handleRadioChange}
					validationDisplay={validationDisplayTypes.HIDDEN}
				/>
				<Grid container>
					<Grid size={12}>
						<Grid container spacing={2}>
							{radio === radioOptionsIds.superior && (
								<Grid size={12}>
									<ComponentLoader fetchStorePath={fetch.constants.urls.userList}>
										<AutocompleteCustom
											size="small"
											fieldName={FIELDS.superior}
											options={sortedSuperiorsOptionsWithPossibleLogin}
											label={getLabelMessage('resolverSettings:userName')}
											labelPropName="name"
											valuePropName="code"
											required={radio === radioOptionsIds.superior}
										/>
									</ComponentLoader>
								</Grid>
							)}

							{radio === radioOptionsIds.subordinate && (
								<Grid size={12}>
									<ComponentLoader fetchStorePath={fetch.constants.urls.userList}>
										<AutocompleteCustom
											size="small"
											fieldName={FIELDS.subordinate}
											options={sortedSubordinateUsersOptionsWithPossibleLogin}
											label={getLabelMessage('resolverSettings:userName')}
											labelPropName="name"
											valuePropName="code"
											required={radio === radioOptionsIds.subordinate}
										/>
									</ComponentLoader>
								</Grid>
							)}

							{radio === radioOptionsIds.users && (
								<>
									<Grid size={3}>
										<TextFieldWithSearchIcon
											fieldName={FIELDS.login}
											label={getLabelMessage('resolverSettings:login')}
											onSearch={handleSearchUser}
											onChange={handleUserFieldChange}
											maxLength={MaxLengths.LOGIN}
											required={
												radio === radioOptionsIds.users &&
												all(isNilOrEmpty)([formData?.sellerIn, formData?.userSurname])
											}
											validationDisplay={
												!formData?.login ? validationDisplayTypes.HIDDEN : undefined
											}
										/>
									</Grid>
									<Tooltip title={getLabelMessage('tooltips:sellerInRange')}>
										<Grid size={3}>
											<TextFieldWithSearchIcon
												fieldName={FIELDS.sellerIn}
												label={getLabelMessage('resolverSettings:sellerIn')}
												onSearch={handleSearchUser}
												onChange={handleUserFieldChange}
												maxLength={MaxLengths.SELLER_IN}
												required={
													radio === radioOptionsIds.users &&
													all(isNilOrEmpty)([formData?.userSurname, formData?.login])
												}
												validationDisplay={
													!formData?.sellerIn ? validationDisplayTypes.HIDDEN : undefined
												}
											/>
										</Grid>
									</Tooltip>
									<Grid size={3}>
										<TextField
											size="small"
											fieldName={FIELDS.userFirstName}
											label={getLabelMessage('resolverSettings:userFirstName')}
											onChange={handleUserFieldChange}
											slotProps={{
												htmlInput: {
													maxLength: MaxLengths.NAME,
												},
											}}
										/>
									</Grid>
									<Grid size={3}>
										<TextFieldWithSearchIcon
											fieldName={FIELDS.userSurname}
											label={getLabelMessage('resolverSettings:userSurname')}
											onSearch={handleSearchUser}
											onChange={handleUserFieldChange}
											maxLength={MaxLengths.SURNAME}
											required={
												radio === radioOptionsIds.users &&
												all(isNilOrEmpty)([formData?.sellerIn, formData?.login])
											}
											validationDisplay={
												!formData?.userSurname ? validationDisplayTypes.HIDDEN : undefined
											}
										/>
									</Grid>
								</>
							)}

							{radio === radioOptionsIds.agency && (
								<>
									<Grid size={4}>
										<ComponentLoader
											fetchStorePath={`${fetch.constants.urls.orgStructureRegions}`}
											ignoreErrors
										>
											<AutocompleteCustom
												size="small"
												fieldName={FIELDS.region}
												label={getLabelMessage('resolverSettings:idkRegion')}
												options={filteredRegions}
												labelPropName="description"
												valuePropName="regionId"
												onChange={handleAgencyReset}
												required={radio === radioOptionsIds.agency}
											/>
										</ComponentLoader>
									</Grid>
									<Grid size={4}>
										<AutocompleteCustom
											size="small"
											fieldName={FIELDS.agency}
											label={getLabelMessage('resolverSettings:idkAgency')}
											options={filteredAgencies}
											labelPropName="description"
											valuePropName="groupName"
											disabled={isNilOrEmptyString(region)}
											required={radio === radioOptionsIds.agency}
										/>
									</Grid>
								</>
							)}
						</Grid>
					</Grid>
				</Grid>
			</Stack>
		</Paper>
	);
};

ResolverSettings.propTypes = {
	formName: PropTypes.string,
	idkAgencies: PropTypes.array,
	idkRegions: PropTypes.array,
	showNonUniqueSolverDialog: PropTypes.bool,
	showSectionHeader: PropTypes.bool,
};

export default compose(
	codelistProvider(codelists.idkRegions, 'idkRegions'),
	codelistProvider(codelists.idkAgencies, 'idkAgencies'),
)(ResolverSettings);
